import { View, Platform } from 'react-native';
import Head from 'expo-router/head';
import { Cookie } from 'lucide-react-native';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import {
  CounselReviewBanner,
  LastUpdated,
  LegalBullet,
  LegalContactCard,
  LegalLink,
  LegalParagraph,
  LegalSection,
  LegalSubheading,
  StorageTable,
  Strong,
  Code,
} from '@/components/legal/LegalBlocks';
import { ANALYTICS, COOKIE_POLICY_UPDATED, FUNCTIONAL, STRICTLY_NECESSARY } from '@/lib/legal/cookie-storage';

// Content mirrors the master document at docs/legal/cookie-policy.md — update both together.
// The storage inventory lives in src/lib/legal/cookie-storage.ts;
// src/app/__tests__/cookies-screen.test.tsx fails if a key in the document is
// missing there, or if a persisted store in src/ is missing from the document.

export default function CookiePolicyScreen() {
  useTrackScreen('CookiePolicy');
  const colors = usePublicThemeColors();

  return (
    <PublicLayout title="Cookie Policy" titleIcon={<Cookie size={22} color={colors.primary[600]} />}>
      {Platform.OS === 'web' && (
        <Head>
          <title>Cookie Policy — Inverio</title>
          <meta
            name="description"
            content="Inverio sets no cookies on its domain. What we keep in your browser or app storage, why, for how long, and how to switch off usage analytics."
          />
          <link rel="canonical" href="https://inverio.ai/cookies" />
          <meta property="og:title" content="Cookie Policy — Inverio" />
          <meta
            property="og:description"
            content="Inverio sets no cookies on its domain. What we keep in your browser or app storage, why, for how long, and how to switch off usage analytics."
          />
          <meta property="og:url" content="https://inverio.ai/cookies" />
        </Head>
      )}

      <LastUpdated date={COOKIE_POLICY_UPDATED} />
      <CounselReviewBanner />

      <View className="mb-6">
        <LegalParagraph>
          This Cookie Policy explains what Inverio stores in your browser or on your device when you use our website, web
          application and mobile apps (the "Service"), why, for how long, and how you can remove it. It supplements our{' '}
          <LegalLink href="/privacy">Privacy Policy</LegalLink>, which explains how we handle personal data generally.
        </LegalParagraph>
      </View>

      <LegalSection number={1} title="The short version" delay={200}>
        <LegalBullet>
          <Strong>We set no cookies on inverio.ai.</Strong> Not for sign-in, not for analytics, not for advertising.
        </LegalBullet>
        <LegalBullet>
          Instead, the Service keeps a small amount of data in your browser's <Strong>local storage, session storage and
          IndexedDB</Strong> (web), or in the app's <Strong>storage and secure storage</Strong> (iOS). This data stays on your
          device; most of it is needed for the Service to work.
        </LegalBullet>
        <LegalBullet>
          We use <Strong>no advertising, no third-party analytics and no tracking across other sites</Strong>.
        </LegalBullet>
        <LegalBullet>
          We do collect <Strong>first-party usage analytics</Strong> (which screens and features you use). You can switch it
          off at any time in Settings → Your Data → <Strong>Usage analytics</Strong>.
        </LegalBullet>
      </LegalSection>

      <LegalSection number={2} title="What we store" delay={250}>
        <LegalParagraph>
          "First party" means the data is stored by Inverio under our own domain or in our app. "Third party" means it is
          stored by another company under its own domain.
        </LegalParagraph>

        <LegalSubheading>Strictly necessary</LegalSubheading>
        <LegalParagraph>
          These are needed to sign you in and keep the Service working. They cannot be switched off without stopping the
          Service from working.
        </LegalParagraph>
        <StorageTable label="Strictly necessary storage" rows={STRICTLY_NECESSARY} />

        <LegalSubheading>Functional</LegalSubheading>
        <LegalParagraph>
          These remember your choices and keep a local copy of your own account data so the Service loads quickly.
        </LegalParagraph>
        <StorageTable label="Functional storage" rows={FUNCTIONAL} />
        <LegalParagraph>
          Devices that used a much older version of the app may also hold a legacy <Code>inverio-storage</Code> entry, which the app
          migrated automatically.
        </LegalParagraph>

        <LegalSubheading>Analytics</LegalSubheading>
        <StorageTable label="Analytics storage" rows={ANALYTICS} />
        <LegalParagraph>
          Usage analytics is not needed to provide the Service. It is <Strong>on by default</Strong> and you can switch it off
          at any time (Section 4). Until the app has read your choice from your account, it sends nothing and stores no
          events.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={3} title="Third parties" delay={300}>
        <LegalParagraph>
          We embed no third-party advertising, social-media or analytics scripts. A few services are involved in specific
          flows:
        </LegalParagraph>
        <LegalBullet>
          <Strong>Cloudflare Turnstile</Strong> — a security check that protects our waitlist, contact and pitch-submission
          forms from bots. It runs in a frame served by Cloudflare (challenges.cloudflare.com). Anything it stores in your
          browser is held under Cloudflare's own domain, not ours, and is governed by Cloudflare's privacy terms.
        </LegalBullet>
        <LegalBullet>
          <Strong>Sentry</Strong> — error monitoring, active only in our production app. It does not record your screen or
          sessions, and error reports are scrubbed of your email, IP address, device name and any tokens before they are
          sent. It sets no cookies on our domain.
        </LegalBullet>
        <LegalBullet>
          <Strong>Stripe, LinkedIn, Google and Dropbox</Strong> — when you pay, sign in with LinkedIn, or connect Google Drive
          or Dropbox, you are sent to that company's own page. Any cookies they set stay on their own domains and are
          governed by their own policies.
        </LegalBullet>
        <LegalParagraph>The Service does not use a service worker or any other offline cache in your browser.</LegalParagraph>
      </LegalSection>

      <LegalSection number={4} title="Your choices" delay={350}>
        <LegalParagraph>
          <Strong>Switch off usage analytics.</Strong> Go to Settings → Your Data and turn off{' '}
          <Strong>Usage analytics</Strong> (the same switch is in Manage my data). The choice is saved to your account, so it
          applies on every device and browser you sign in on, and our database refuses new usage events for an account that
          has switched it off. From that moment no further usage events or session records are sent, and events waiting to
          be sent are deleted from the device. Data already sent stays in our database under our Privacy Policy; you can
          delete it in Manage my data, and it is deleted with your account.
        </LegalParagraph>
        <LegalParagraph>
          <Strong>Sign out.</Strong> Signing out removes your sign-in token and empties the cached account data listed above.
          Your theme is kept, because it is a setting for the device rather than data about you. Your Usage analytics choice
          stays with your account and applies again when you sign back in.
        </LegalParagraph>
        <LegalParagraph>
          <Strong>Clear stored data.</Strong>
        </LegalParagraph>
        <LegalBullet>
          Web: use your browser's settings to clear site data for inverio.ai (often under "Cookies and site data" or
          "Storage"). This removes everything in Section 2 for our site, including the encryption key, and signs you out.
        </LegalBullet>
        <LegalBullet>
          iOS: deleting the app removes its app storage. Items in the iOS Keychain (secure storage) can survive deleting the
          app, so sign out first if you want your sign-in token removed from the device.
        </LegalBullet>
      </LegalSection>

      <LegalSection number={5} title="Changes" delay={400}>
        <LegalParagraph>
          If we start using cookies, or add storage that is not strictly necessary, we will update this policy before doing
          so and, where the law requires it, ask for your consent first. The date above shows the latest revision.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={6} title="Contact" delay={450}>
        <LegalContactCard
          lines={[
            'Inverio — a service of A. Kouadio INVARK, Switzerland',
            'Questions about this policy or your data: privacy@inverio.ai',
          ]}
        />
      </LegalSection>
    </PublicLayout>
  );
}
